import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../pages/AuthContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faShoppingCart, faUser, faPhone, faMapMarkerAlt, faCalendarAlt,
    faFileInvoice, faBox, faRupeeSign, faTag, faBalanceScale, faDollarSign,
    faCalculator, faExclamationTriangle, faPlus, faMinus, faTrash, faSave,
    faTimes, faSpinner, faCheckCircle, faExclamationCircle, faSearch, faGlobe, faQrcode,
    faBarcode, faCreditCard, faMoneyBill, faPercent, faReceipt, faChevronDown,
    faChevronUp, faBoxes, faHistory, faPrint, faFilePdf, faDownload,
    faUpload, faEdit, faChartLine, faTimesCircle, faShoppingBag,
    faAddressCard, faIdCard, faStore, faEnvelope, faBuilding,
    faPalette, faRuler, faLayerGroup, faUsers, faIdBadge, faTruck,
    faCheckDouble, faClock, faHourglassHalf
} from '@fortawesome/free-solid-svg-icons';

// ==============================================
// Translation Utility
// ==============================================
const createFieldOrdersTranslator = () => {
    let cache = new Map();
    let translationListeners = new Set();

    const loadFromCache = () => {
        try {
            const cached = localStorage.getItem('ourzest_field_orders_translations');
            if (cached) {
                const parsed = JSON.parse(cached);
                Object.entries(parsed).forEach(([key, value]) => {
                    cache.set(key, value);
                });
            }
        } catch (e) {
            console.log("No cache found in Field Orders Form");
        }
    };

    const saveToCache = () => {
        try {
            const cacheObj = {};
            cache.forEach((value, key) => {
                cacheObj[key] = value;
            });
            localStorage.setItem('ourzest_field_orders_translations', JSON.stringify(cacheObj));
        } catch (e) {
            console.error("Error saving to cache:", e);
        }
    };

    const translateAPI = async (text, targetLang) => {
        if (targetLang === 'en') return text;
        try {
            const response = await fetch('https://translate.argosopentech.com/translate', {
                method: 'POST',
                body: JSON.stringify({ q: text, source: 'en', target: targetLang }),
                headers: { 'Content-Type': 'application/json' }
            });
            if (response.ok) {
                const data = await response.json();
                return data.translatedText;
            }
        } catch (e) {
            console.log("LibreTranslate failed, trying MyMemory");
        }
        try {
            const response = await fetch(
                `https://api.mymemory.translated.net/get?q=${encodeURIComponent(text)}&langpair=en|${targetLang}`
            );
            if (response.ok) {
                const data = await response.json();
                return data.responseData.translatedText;
            }
        } catch (e) {
            console.log("MyMemory translation failed");
        }
        return text;
    };

    const translateText = async (text, targetLang) => {
        const cacheKey = `${text}_${targetLang}`;
        if (cache.has(cacheKey)) {
            return cache.get(cacheKey);
        }
        const translated = await translateAPI(text, targetLang);
        cache.set(cacheKey, translated);
        saveToCache();
        return translated;
    };

    const defaultTexts = {
        newFieldOrder: 'New Field Order',
        editFieldOrder: 'Edit Field Order',
        orderDetails: 'Field Order Details',
        createFieldOrder: 'Create Field Order',
        orderNumber: 'Order Number',
        date: 'Date',
        shop: 'Shop',
        customerDetails: 'Customer Details',
        customerName: 'Customer Name',
        customerPhone: 'Phone Number',
        customerAddress: 'Address',
        customerGST: 'GST Number',
        searchCustomer: 'Search customer by name or phone...',
        searchProducts: 'Search Fabric Products',
        searchPlaceholder: 'Search by name, color, batch...',
        selectProduct: 'Select Product',
        noProductsFound: 'No products found',
        outOfStock: 'Out of Stock',
        lowStock: 'Low Stock',
        availableStock: 'Available',
        items: 'Items',
        addItem: 'Add Item',
        productName: 'Product',
        colorCode: 'Color Code',
        batchNumber: 'Batch No',
        type: 'Type',
        brand: 'Brand',
        unit: 'Unit',
        price: 'Price',
        quantity: 'Qty',
        total: 'Total',
        remove: 'Remove',
        stock: 'Stock',
        calculations: 'Calculations',
        subtotal: 'Subtotal',
        discountPercent: 'Discount %',
        discountAmount: 'Discount Amount',
        taxPercent: 'Tax %',
        taxAmount: 'Tax Amount',
        grandTotal: 'Grand Total',
        orderStatus: 'Order Status',
        pending: 'Pending',
        processing: 'Processing',
        approved: 'Approved',
        delivered: 'Delivered',
        completed: 'Completed',
        cancelled: 'Cancelled',
        orderNotes: 'Order Notes',
        createOrder: 'Create Order',
        updateOrder: 'Update Order',
        clear: 'Clear',
        saving: 'Saving...',
        loading: 'Loading...',
        orderCreated: 'Field order created successfully!',
        orderUpdated: 'Field order updated successfully!',
        statusUpdated: 'Order status updated successfully!',
        failedSave: 'Failed to save field order',
        failedUpdate: 'Failed to update field order',
        networkError: 'Network error occurred',
        duplicateSubmission: 'Duplicate submission detected. Please wait.',
        noShopSelected: 'No shop selected. Please select a shop first.',
        itemsRequired: 'At least one item is required',
        quantityExceedsStock: 'Quantity exceeds available stock',
        invalidQuantity: 'Please enter a valid quantity',
        invalidPrice: 'Please enter a valid price',
        yards: 'Yards',
        meters: 'Meters',
        rolls: 'Rolls',
        success: 'Success',
        error: 'Error',
        warning: 'Warning',
        totalOrders: 'Total Orders',
        totalAmount: 'Total Amount',
        notes: 'Notes',
        statusHistory: 'Status History',
        orderInfo: 'Order Information',
        statusChangedBy: 'Changed by',
        statusChangedOn: 'Changed on',
        noCustomersFound: 'No customers found',
        time: 'Time',
        rollsUsed: 'Rolls Used',
        yardsPerRoll: 'Yards/Roll'
    };

    const getTranslations = async (lang) => {
        if (lang === 'en') {
            return defaultTexts;
        }
        const translations = {};
        const promises = [];
        Object.keys(defaultTexts).forEach(key => {
            const cacheKey = `${defaultTexts[key]}_${lang}`;
            if (cache.has(cacheKey)) {
                translations[key] = cache.get(cacheKey);
            } else {
                promises.push(
                    translateText(defaultTexts[key], lang).then(translated => {
                        translations[key] = translated;
                    })
                );
            }
        });
        if (promises.length > 0) {
            await Promise.all(promises);
            saveToCache();
        }
        return translations;
    };

    const addListener = (callback) => {
        translationListeners.add(callback);
    };

    const removeListener = (callback) => {
        translationListeners.delete(callback);
    };

    const notifyListeners = (lang) => {
        translationListeners.forEach(callback => callback(lang));
    };

    const checkLanguageChange = () => {
        const currentLang = localStorage.getItem('ourzest_language') || 'en';
        notifyListeners(currentLang);
    };

    const startLanguageCheck = () => {
        checkLanguageChange();
        const intervalId = setInterval(checkLanguageChange, 500);
        window.addEventListener('storage', checkLanguageChange);
        return () => {
            clearInterval(intervalId);
            window.removeEventListener('storage', checkLanguageChange);
        };
    };

    loadFromCache();
    return {
        getTranslations,
        translateText,
        addListener,
        removeListener,
        startLanguageCheck
    };
};

const fieldOrdersTranslator = createFieldOrdersTranslator();

// ==============================================
// Debounce Function
// ==============================================
function debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
        const later = () => {
            clearTimeout(timeout);
            func(...args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
    };
}

// ==============================================
// Toast Notification Component
// ==============================================
const ToastNotification = ({ toast, setToast, translations }) => {
    const getIconForType = (type) => {
        switch (type) {
            case 'success': return faCheckCircle;
            case 'error': return faExclamationCircle;
            case 'warning': return faExclamationTriangle;
            default: return faCheckCircle;
        }
    };
    const getTitleForType = (type) => {
        switch (type) {
            case 'success': return translations?.success || 'Success';
            case 'error': return translations?.error || 'Error';
            case 'warning': return translations?.warning || 'Warning';
            default: return '';
        }
    };
    if (!toast.show) return null;
    const opacity = Math.max(toast.progress / 100, 0.1);
    return (
        <div className={`toast-notification ${toast.type} ${toast.direction === 'rtl' ? 'toast-rtl' : 'toast-ltr'}`} style={{ opacity }}>
            <div className="toast-content">
                <div className="toast-icon"><FontAwesomeIcon icon={getIconForType(toast.type)} /></div>
                <div className="toast-text">
                    <div className="toast-title">{getTitleForType(toast.type)}</div>
                    <div className="toast-message">{toast.message}</div>
                </div>
                <button className="toast-close" onClick={() => setToast(prev => ({ ...prev, show: false }))}>
                    <FontAwesomeIcon icon={faTimes} />
                </button>
            </div>
            <div className="toast-timer">
                <div className="toast-progress-bar" style={{ width: `${toast.progress}%`, transition: 'width 0.1s linear' }}></div>
                <div className="toast-countdown">{toast.timeLeft}s</div>
            </div>
        </div>
    );
};

// ==============================================
// Order Status Badge Component
// ==============================================
const OrderStatusBadge = ({ status, translations }) => {
    const statusMap = {
        pending: { icon: faClock, label: 'Pending' },
        processing: { icon: faHourglassHalf, label: 'Processing' },
        approved: { icon: faCheckCircle, label: 'Approved' },
        delivered: { icon: faTruck, label: 'Delivered' },
        completed: { icon: faCheckDouble, label: 'Completed' },
        cancelled: { icon: faTimesCircle, label: 'Cancelled' }
    };
    const s = statusMap[status] || statusMap.pending;
    return (
        <div className={`order-status-badge ${status}`}>
            <FontAwesomeIcon icon={s.icon} />
            <span>{translations?.[status] || s.label}</span>
        </div>
    );
};

// ==============================================
// Customer Search Component
// ==============================================
const CustomerSearch = ({ onSelect, selectedCustomerId, selectedCustomerName, disabled, translations, isRTL, shopId, user, showToast }) => {
    const [searchTerm, setSearchTerm] = useState(selectedCustomerName || '');
    const [customerSearchResults, setCustomerSearchResults] = useState([]);
    const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
    const [searchingCustomer, setSearchingCustomer] = useState(false);
    const searchRef = useRef(null);

    const searchCustomers = useCallback(debounce(async (term) => {
        if (!user?.username || !shopId) return;
        if (!term || term.trim().length < 2) {
            setCustomerSearchResults([]);
            setShowCustomerDropdown(false);
            return;
        }
        setSearchingCustomer(true);
        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/sell.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'search_customer',
                    search_term: term,
                    username: user.username,
                    shop_id: shopId
                })
            });
            const data = await response.json();
            if (data.status === 'success') {
                const customers = data.data || [];
                setCustomerSearchResults(customers);
                setShowCustomerDropdown(customers.length > 0);
            } else {
                setCustomerSearchResults([]);
                setShowCustomerDropdown(false);
            }
        } catch (error) {
            console.error('Error searching customers:', error);
            setCustomerSearchResults([]);
            setShowCustomerDropdown(false);
        } finally {
            setSearchingCustomer(false);
        }
    }, 250), [user?.username, shopId]);

    const handleInputChange = (e) => {
        const value = e.target.value;
        setSearchTerm(value);
        if (value.length >= 2) {
            searchCustomers(value);
        } else {
            setCustomerSearchResults([]);
            setShowCustomerDropdown(false);
        }
    };

    const handleSelect = (customer) => {
        setSearchTerm(customer.customer_name);
        setShowCustomerDropdown(false);
        if (onSelect) {
            onSelect(customer);
        }
        if (showToast) {
            showToast('success', `${translations?.customerSelected || 'Customer selected'}: ${customer.customer_name}`);
        }
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (searchRef.current && !searchRef.current.contains(event.target)) {
                setShowCustomerDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        if (selectedCustomerName) {
            setSearchTerm(selectedCustomerName);
        }
    }, [selectedCustomerName]);

    return (
        <div className="form-group-modern customer-search-group" ref={searchRef}>
            <div className="input-container">
                <FontAwesomeIcon icon={faUser} className="input-icon" />
                <input
                    type="text"
                    id="customer_name"
                    name="customer_name"
                    value={searchTerm}
                    onChange={handleInputChange}
                    onFocus={() => searchTerm.length >= 2 && setShowCustomerDropdown(true)}
                    placeholder=" "
                    disabled={disabled}
                    dir={isRTL ? 'rtl' : 'ltr'}
                    autoComplete="off"
                />
                <label htmlFor="customer_name" className="input-label">
                    {translations?.customerName || 'Customer Name'}
                </label>
                {searchingCustomer && (
                    <div className="search-indicator">
                        <FontAwesomeIcon icon={faSpinner} spin />
                    </div>
                )}
            </div>
            {showCustomerDropdown && customerSearchResults.length > 0 && (
                <div className="customer-search-dropdown">
                    {customerSearchResults.map(customer => (
                        <div key={customer.customerid || customer.customer_id} className="customer-search-item" onClick={() => handleSelect(customer)}>
                            <div className="customer-name">{customer.customer_name || customer.name}</div>
                            {(customer.phone || customer.phonenumber) && (
                                <div className="customer-phone">
                                    <FontAwesomeIcon icon={faPhone} size="xs" />
                                    {customer.phone || customer.phonenumber}
                                </div>
                            )}
                            {(customer.customer_code) && (
                                <div className="customer-code">
                                    <FontAwesomeIcon icon={faIdCard} size="xs" />
                                    {customer.customer_code}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}
            {showCustomerDropdown && customerSearchResults.length === 0 && !searchingCustomer && searchTerm.length >= 2 && (
                <div className="customer-search-dropdown empty">
                    <FontAwesomeIcon icon={faExclamationTriangle} />
                    <span>{translations?.noCustomersFound || 'No customers found'}</span>
                </div>
            )}
        </div>
    );
};

// ==============================================
// Product Search Component with Stock Display and Rolls
// ==============================================
const ProductSearch = ({ onSelect, disabled, translations, isRTL, shopId, user, showToast }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [filteredProducts, setFilteredProducts] = useState([]);
    const [showProductDropdown, setShowProductDropdown] = useState(false);
    const [searchingProducts, setSearchingProducts] = useState(false);
    const searchRef = useRef(null);

    const searchProducts = useCallback(debounce(async (term) => {
        if (!user?.username || !shopId) return;
        if (!term || term.trim().length < 2) {
            setFilteredProducts([]);
            setShowProductDropdown(false);
            return;
        }
        setSearchingProducts(true);
        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/field_orders.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'search_fab_products',
                    searchTerm: term,
                    username: user.username,
                    shop_id: shopId
                })
            });
            const data = await response.json();
            if (data.status === 'success') {
                const products = data.data || [];
                const processedProducts = products.map(product => ({
                    id: product.id,
                    product_name: product.product_name || '',
                    product_colorcode: product.product_colorcode || '',
                    batch_no: product.batch_no || '',
                    type: product.type || '',
                    brand: product.brand || '',
                    unit: product.unit || 'yards',
                    total_yards: parseFloat(product.total_yards) || 0,
                    total_meters: parseFloat(product.total_meters) || 0,
                    total_rolls: parseInt(product.total_rolls) || 0,
                    stock_alert: parseFloat(product.stock_alert) || 0
                }));
                setFilteredProducts(processedProducts);
                setShowProductDropdown(processedProducts.length > 0);
            } else {
                setFilteredProducts([]);
                setShowProductDropdown(false);
            }
        } catch (error) {
            console.error('Error searching products:', error);
            setFilteredProducts([]);
            setShowProductDropdown(false);
        } finally {
            setSearchingProducts(false);
        }
    }, 250), [user?.username, shopId]);

    const handleInputChange = (e) => {
        const value = e.target.value;
        setSearchTerm(value);
        if (value.length >= 2) {
            searchProducts(value);
        } else {
            setFilteredProducts([]);
            setShowProductDropdown(false);
        }
    };

    const handleSelect = (product) => {
        setSearchTerm('');
        setShowProductDropdown(false);
        if (onSelect) {
            onSelect(product);
        }
        if (showToast) {
            showToast('success', `${product.product_name} added to order`);
        }
    };

    const formatNumber = (value, decimals = 2) => {
        if (value === null || value === undefined || isNaN(value)) return '0.00';
        return parseFloat(value).toFixed(decimals);
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (searchRef.current && !searchRef.current.contains(event.target)) {
                setShowProductDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    return (
        <div className="form-group-modern product-search-group" ref={searchRef}>
            <div className="input-container">
                <FontAwesomeIcon icon={faSearch} className="input-icon search-icon" />
                <input
                    type="text"
                    id="product_search"
                    value={searchTerm}
                    onChange={handleInputChange}
                    onFocus={() => searchTerm.length >= 2 && setShowProductDropdown(true)}
                    placeholder=" "
                    disabled={disabled}
                    dir={isRTL ? 'rtl' : 'ltr'}
                    className="product-search-input"
                    autoComplete="off"
                />
                <label htmlFor="product_search" className="input-label">
                    {translations?.searchProducts || 'Search Fabric Products'}
                </label>
                {searchingProducts && (
                    <div className="search-indicator">
                        <FontAwesomeIcon icon={faSpinner} spin />
                    </div>
                )}
            </div>
            {showProductDropdown && filteredProducts.length > 0 && (
                <div className="product-search-dropdown fab-product-dropdown">
                    {filteredProducts.map(product => {
                        const availableYards = product.total_yards;
                        const availableMeters = product.total_meters;
                        const availableRolls = product.total_rolls;
                        const hasStock = availableYards > 0 || availableMeters > 0;
                        const isOutOfStock = !hasStock;
                        
                        return (
                            <div 
                                key={product.id} 
                                className={`product-search-item ${isOutOfStock ? 'out-of-stock' : ''}`} 
                                onClick={() => !isOutOfStock && handleSelect(product)}
                                style={{ opacity: isOutOfStock ? 0.5 : 1, cursor: isOutOfStock ? 'not-allowed' : 'pointer' }}
                            >
                                <div className="product-item-name">
                                    <FontAwesomeIcon icon={faBox} className="product-icon" />
                                    {product.product_name}
                                    {product.product_colorcode && (
                                        <span className="product-color-badge">
                                            <FontAwesomeIcon icon={faPalette} />
                                            {product.product_colorcode}
                                        </span>
                                    )}
                                </div>
                                <div className="product-item-details">
                                    {product.batch_no && (
                                        <span>
                                            <FontAwesomeIcon icon={faTag} /> Batch: {product.batch_no}
                                        </span>
                                    )}
                                    {product.type && (
                                        <span>
                                            <FontAwesomeIcon icon={faLayerGroup} /> Type: {product.type}
                                        </span>
                                    )}
                                    {product.brand && (
                                        <span>
                                            <FontAwesomeIcon icon={faBuilding} /> Brand: {product.brand}
                                        </span>
                                    )}
                                </div>
                                <div className="product-stock-details">
                                    <div className="stock-info-row">
                                        <span className={`stock-badge yards-stock ${availableYards <= product.stock_alert && availableYards > 0 ? 'low-stock' : ''}`}>
                                            <FontAwesomeIcon icon={faRuler} />
                                            Yards: {formatNumber(availableYards)}
                                        </span>
                                        <span className={`stock-badge meters-stock ${availableMeters <= product.stock_alert && availableMeters > 0 ? 'low-stock' : ''}`}>
                                            <FontAwesomeIcon icon={faRuler} />
                                            Meters: {formatNumber(availableMeters)}
                                        </span>
                                    </div>
                                    <div className="stock-info-row">
                                        <span className="stock-badge rolls-stock">
                                            <FontAwesomeIcon icon={faBoxes} />
                                            Rolls: {availableRolls}
                                        </span>
                                        <span className="default-unit-badge">
                                            Default: {product.unit}
                                        </span>
                                    </div>
                                    {isOutOfStock && (
                                        <span className="out-of-stock-warning">
                                            <FontAwesomeIcon icon={faTimesCircle} />
                                            {translations?.outOfStock || 'Out of Stock'}
                                        </span>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
            {showProductDropdown && searchTerm.length >= 2 && filteredProducts.length === 0 && !searchingProducts && (
                <div className="product-search-dropdown empty">
                    <FontAwesomeIcon icon={faExclamationTriangle} />
                    <span>{translations?.noProductsFound || 'No products found'}</span>
                </div>
            )}
        </div>
    );
};

// ==============================================
// Order Items Table Component with Rolls Management
// ==============================================
const OrderItemsTable = ({ items, onItemChange, onRemove, errors, disabled, translations, isRTL }) => {
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

    useEffect(() => {
        const handleResize = () => { setIsMobile(window.innerWidth <= 768); };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const calculateTotal = (price, quantity) => {
        const p = parseFloat(price) || 0;
        const q = parseFloat(quantity) || 0;
        return (p * q).toFixed(2);
    };

    const getAvailableStock = (item) => {
        if (item.unit === 'yards') {
            return item.available_yards || 0;
        } else {
            return item.available_meters || 0;
        }
    };

    // Calculate maximum rolls that can be used based on quantity
    const getMaxRolls = (item) => {
        const availableRolls = item.available_rolls || 0;
        const quantity = parseFloat(item.quantity) || 0;
        const yardsPerRoll = parseFloat(item.yards_per_roll) || 0;
        
        if (yardsPerRoll > 0 && item.unit === 'yards') {
            const rollsFromQuantity = Math.ceil(quantity / yardsPerRoll);
            return Math.min(rollsFromQuantity, availableRolls);
        }
        return availableRolls;
    };

    if (isMobile) {
        return (
            <div className="items-mobile-container">
                <div className="items-header-mobile">
                    <h4><FontAwesomeIcon icon={faBoxes} className="header-icon-only" />{translations?.items || 'Items'}</h4>
                </div>
                <div className="items-cards-container">
                    {items.map((item, index) => {
                        const availableStock = getAvailableStock(item);
                        const stockAlert = item.stock_alert || 0;
                        const isLowStock = availableStock <= stockAlert && availableStock > 0;
                        const maxRolls = getMaxRolls(item);
                        
                        return (
                            <div key={index} className="item-card-mobile">
                                <div className="item-card-header">
                                    <span className="item-number"><FontAwesomeIcon icon={faBox} className="header-icon-only" />Item {index + 1}</span>
                                    <button type="button" className="item-remove-btn-mobile" onClick={() => onRemove(index)} disabled={disabled || items.length === 1}>
                                        <FontAwesomeIcon icon={faTrash} />
                                    </button>
                                </div>
                                <div className="item-card-content">
                                    <div className="mobile-field">
                                        <label>{translations?.productName || 'Product'}</label>
                                        <input type="text" value={item.product_name} readOnly className="readonly-input" dir={isRTL ? 'rtl' : 'ltr'} />
                                    </div>
                                    <div className="mobile-row">
                                        <div className="mobile-field half">
                                            <label>{translations?.colorCode || 'Color Code'}</label>
                                            <input type="text" value={item.product_colorcode || ''} readOnly className="readonly-input" />
                                        </div>
                                        <div className="mobile-field half">
                                            <label>{translations?.batchNumber || 'Batch No'}</label>
                                            <input type="text" value={item.batch_no || ''} readOnly className="readonly-input" />
                                        </div>
                                    </div>
                                    <div className="mobile-row">
                                        <div className="mobile-field half">
                                            <label>{translations?.type || 'Type'}</label>
                                            <input type="text" value={item.type || ''} readOnly className="readonly-input" />
                                        </div>
                                        <div className="mobile-field half">
                                            <label>{translations?.brand || 'Brand'}</label>
                                            <input type="text" value={item.brand || ''} readOnly className="readonly-input" />
                                        </div>
                                    </div>
                                    <div className="mobile-field stock-info">
                                        <label>{translations?.availableStock || 'Available Stock'}</label>
                                        <div className={`stock-badge ${isLowStock ? 'low-stock' : ''}`}>
                                            {availableStock.toFixed(2)} {item.unit}
                                            {item.available_rolls > 0 && ` | ${item.available_rolls} ${translations?.rolls || 'rolls'}`}
                                        </div>
                                    </div>
                                    <div className="mobile-row">
                                        <div className={`mobile-field half ${errors[`unit_${index}`] ? 'has-error' : ''}`}>
                                            <label>{translations?.unit || 'Unit'} *</label>
                                            <select 
                                                value={item.unit} 
                                                onChange={(e) => {
                                                    const newUnit = e.target.value;
                                                    const newAvailableStock = newUnit === 'yards' ? item.available_yards : item.available_meters;
                                                    onItemChange(index, 'unit', newUnit, newAvailableStock);
                                                }} 
                                                disabled={disabled}
                                            >
                                                <option value="yards">{translations?.yards || 'Yards'} ({item.available_yards?.toFixed(2) || '0'})</option>
                                                <option value="meters">{translations?.meters || 'Meters'} ({item.available_meters?.toFixed(2) || '0'})</option>
                                            </select>
                                        </div>
                                        <div className={`mobile-field half ${errors[`price_${index}`] ? 'has-error' : ''}`}>
                                            <label>{translations?.price || 'Price'} *</label>
                                            <input type="number" value={item.price_per_unit} onChange={(e) => onItemChange(index, 'price_per_unit', e.target.value)} step="0.01" min="0" placeholder="0.00" disabled={disabled} dir="ltr" />
                                        </div>
                                    </div>
                                    <div className="mobile-row">
                                        <div className={`mobile-field half ${errors[`quantity_${index}`] ? 'has-error' : ''}`}>
                                            <label>{translations?.quantity || 'Qty'} *</label>
                                            <input 
                                                type="number" 
                                                value={item.quantity} 
                                                onChange={(e) => onItemChange(index, 'quantity', e.target.value)} 
                                                step="0.001" 
                                                min="0.001" 
                                                max={availableStock} 
                                                placeholder="0.000" 
                                                disabled={disabled} 
                                                dir="ltr" 
                                            />
                                            <small>Max: {availableStock.toFixed(2)}</small>
                                        </div>
                                        <div className={`mobile-field half ${errors[`rolls_${index}`] ? 'has-error' : ''}`}>
                                            <label>{translations?.rollsUsed || 'Rolls Used'}</label>
                                            <input 
                                                type="number" 
                                                value={item.rolls_used || 0} 
                                                onChange={(e) => onItemChange(index, 'rolls_used', e.target.value)} 
                                                step="1" 
                                                min="0" 
                                                max={maxRolls} 
                                                placeholder="0" 
                                                disabled={disabled} 
                                                dir="ltr" 
                                            />
                                            <small>Max: {maxRolls}</small>
                                        </div>
                                    </div>
                                    <div className="mobile-row">
                                        <div className="mobile-field half">
                                            <label>{translations?.yardsPerRoll || 'Yards/Roll'}</label>
                                            <input 
                                                type="number" 
                                                value={item.yards_per_roll || 0} 
                                                onChange={(e) => onItemChange(index, 'yards_per_roll', e.target.value)} 
                                                step="0.001" 
                                                min="0" 
                                                placeholder="0.000" 
                                                disabled={disabled} 
                                                dir="ltr" 
                                            />
                                        </div>
                                        <div className="mobile-field half">
                                            <label>{translations?.total || 'Total'}</label>
                                            <input type="text" value={`Rs. ${calculateTotal(item.price_per_unit, item.quantity)}`} readOnly disabled className="readonly-input total-display" />
                                        </div>
                                    </div>
                                    {errors[`quantity_${index}`] && (
                                        <div className="error-text">
                                            <FontAwesomeIcon icon={faExclamationTriangle} />
                                            {errors[`quantity_${index}`]}
                                        </div>
                                    )}
                                    {errors[`rolls_${index}`] && (
                                        <div className="error-text">
                                            <FontAwesomeIcon icon={faExclamationTriangle} />
                                            {errors[`rolls_${index}`]}
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    }

    return (
        <div className="items-table-container">
            <div className="table-header-actions">
                <h4><FontAwesomeIcon icon={faBoxes} className="header-icon-only" />{translations?.items || 'Items'}</h4>
            </div>
            <div className="table-wrapper">
                <table className="items-table fab-items-table">
                    <thead>
                        <tr>
                            <th className="product-col">{translations?.productName || 'Product'} <span className="required">*</span></th>
                            <th className="color-col">{translations?.colorCode || 'Color Code'}</th>
                            <th className="batch-col">{translations?.batchNumber || 'Batch No'}</th>
                            <th className="type-col">{translations?.type || 'Type'}</th>
                            <th className="brand-col">{translations?.brand || 'Brand'}</th>
                            <th className="unit-col">{translations?.unit || 'Unit'} <span className="required">*</span></th>
                            <th className="price-col">{translations?.price || 'Price'} <span className="required">*</span></th>
                            <th className="qty-col">{translations?.quantity || 'Qty'} <span className="required">*</span></th>
                            <th className="rolls-col">{translations?.rolls || 'Rolls'}</th>
                            <th className="total-col">{translations?.total || 'Total'}</th>
                            <th className="action-col"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.map((item, index) => {
                            const availableStock = getAvailableStock(item);
                            const stockAlert = item.stock_alert || 0;
                            const isLowStock = availableStock <= stockAlert && availableStock > 0;
                            const maxRolls = getMaxRolls(item);
                            
                            return (
                                <tr key={index} className={errors[`quantity_${index}`] || errors[`rolls_${index}`] ? 'error-row' : ''}>
                                    <td>
                                        <div className="table-field">
                                            <input type="text" value={item.product_name} readOnly className="readonly-input" dir={isRTL ? 'rtl' : 'ltr'} />
                                        </div>
                                    </td>
                                    <td>
                                        <div className="table-field">
                                            <input type="text" value={item.product_colorcode || ''} readOnly className="readonly-input" />
                                        </div>
                                    </td>
                                    <td>
                                        <div className="table-field">
                                            <input type="text" value={item.batch_no || ''} readOnly className="readonly-input" />
                                        </div>
                                    </td>
                                    <td>
                                        <div className="table-field">
                                            <input type="text" value={item.type || ''} readOnly className="readonly-input" />
                                        </div>
                                    </td>
                                    <td>
                                        <div className="table-field">
                                            <input type="text" value={item.brand || ''} readOnly className="readonly-input" />
                                        </div>
                                    </td>
                                    <td>
                                        <div className="table-field">
                                            <select 
                                                value={item.unit} 
                                                onChange={(e) => {
                                                    const newUnit = e.target.value;
                                                    const newAvailableStock = newUnit === 'yards' ? item.available_yards : item.available_meters;
                                                    onItemChange(index, 'unit', newUnit, newAvailableStock);
                                                }} 
                                                disabled={disabled}
                                            >
                                                <option value="yards">{translations?.yards || 'Yards'} ({item.available_yards?.toFixed(2) || '0'})</option>
                                                <option value="meters">{translations?.meters || 'Meters'} ({item.available_meters?.toFixed(2) || '0'})</option>
                                            </select>
                                        </div>
                                    </td>
                                    <td>
                                        <div className="table-field">
                                            <input type="number" value={item.price_per_unit} onChange={(e) => onItemChange(index, 'price_per_unit', e.target.value)} step="0.01" min="0" placeholder="0.00" disabled={disabled} dir="ltr" />
                                        </div>
                                    </td>
                                    <td>
                                        <div className="table-field">
                                            <input 
                                                type="number" 
                                                value={item.quantity} 
                                                onChange={(e) => onItemChange(index, 'quantity', e.target.value)} 
                                                step="0.001" 
                                                min="0.001" 
                                                max={availableStock} 
                                                placeholder="0.000" 
                                                disabled={disabled} 
                                                dir="ltr" 
                                            />
                                            {/* <small className="stock-hint">
                                                {translations?.stock || 'Stock'}: {availableStock.toFixed(2)} {item.unit}
                                                {item.available_rolls > 0 && ` | ${item.available_rolls} ${translations?.rolls || 'rolls'}`}
                                                {isLowStock && (
                                                    <span className="low-stock-hint"> ⚠️ Low Stock!</span>
                                                )}
                                            </small> */}
                                            {errors[`quantity_${index}`] && (
                                                <div className="table-error">
                                                    <FontAwesomeIcon icon={faExclamationTriangle} />
                                                    {errors[`quantity_${index}`]}
                                                </div>
                                            )}
                                        </div>
                                    </td>
                                    <td>
                                        <div className="table-field">
                                            <input 
                                                type="number" 
                                                value={item.rolls_used || 0} 
                                                onChange={(e) => onItemChange(index, 'rolls_used', e.target.value)} 
                                                step="1" 
                                                min="0" 
                                                max={maxRolls} 
                                                placeholder="0" 
                                                disabled={disabled} 
                                                dir="ltr" 
                                            />
                                            {/* <small className="stock-hint">Max: {maxRolls}</small> */}
                                            {errors[`rolls_${index}`] && (
                                                <div className="table-error">
                                                    <FontAwesomeIcon icon={faExclamationTriangle} />
                                                    {errors[`rolls_${index}`]}
                                                </div>
                                            )}
                                        </div>
                                    </td>
                                
                                    <td>
                                        <div className="table-field">
                                            <input type="text" value={`Rs. ${calculateTotal(item.price_per_unit, item.quantity)}`} readOnly disabled className="readonly-input total-display" />
                                        </div>
                                    </td>
                                    <td>
                                        <button type="button" className="table-remove-btn" onClick={() => onRemove(index)} disabled={disabled || items.length === 1} title={translations?.remove || 'Remove'}>
                                            <FontAwesomeIcon icon={faTrash} />
                                        </button>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

// ==============================================
// Business Info Component
// ==============================================
const BusinessInfo = ({ businessInfo, translations }) => {
    if (!businessInfo) return null;
    return (
        <div className="business-info-card" style={{ background: 'linear-gradient(135deg, #f8fafc, #f1f5f9)', borderRadius: '12px', display: 'flex', alignItems: 'center' }}>
            {businessInfo.bussinesslogo && (<img src={businessInfo.bussinesslogo} alt="Logo" style={{ width: '50px', height: '50px', objectFit: 'contain', borderRadius: '8px' }} />)}
            <div className="business-details">
                <div style={{ fontSize: '16px', fontWeight: '700', color: '#0f172a' }}>{businessInfo.businessname}</div>
                {businessInfo.businessaddress && (<div style={{ fontSize: '12px', color: '#64748b', marginTop: '2px' }}><FontAwesomeIcon icon={faMapMarkerAlt} size="xs" style={{ marginRight: '5px' }} />{businessInfo.businessaddress}</div>)}
                {businessInfo.phonenumber && (<div style={{ fontSize: '12px', color: '#64748b', marginTop: '2px' }}><FontAwesomeIcon icon={faPhone} size="xs" style={{ marginRight: '5px' }} />{businessInfo.phonenumber}</div>)}
            </div>
        </div>
    );
};

// ==============================================
// Main Field Orders Component
// ==============================================
const Field_Orders = ({ orderId = null, onSuccess = null }) => {
    const { user, currentShop } = useAuth();
    const [shopId, setShopId] = useState(() => {
        if (user?.shop_id) return user.shop_id;
        return localStorage.getItem('current_shop_id') || '';
    });
    const [shopInfo, setShopInfo] = useState({ id: shopId, name: currentShop?.shop_name || localStorage.getItem('current_shop_name') || '' });
    const [businessInfo, setBusinessInfo] = useState(null);
    const [formData, setFormData] = useState({
        order_date: new Date().toISOString().split('T')[0],
        order_number: '',
        order_number_int: 0,
        customer_id: null,
        customer_name: '',
        customer_phone: '',
        customer_address: '',
        customer_gst: '',
        subtotal: '0.00',
        discount_percent: '0.00',
        discount_amount: '0.00',
        tax_percent: '0.00',
        tax_amount: '0.00',
        grand_total: '0.00',
        status: 'pending',
        notes: '',
        items: [],
        themeColor: localStorage.getItem('ourzest_theme') || '#8b5cf6'
    });
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [errors, setErrors] = useState({});
    const [currentLanguage, setCurrentLanguage] = useState(() => {
        return localStorage.getItem('ourzest_language') || 'en';
    });
    const [translations, setTranslations] = useState({});
    const [isTranslating, setIsTranslating] = useState(false);
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [toast, setToast] = useState({ show: false, type: '', message: '', direction: 'ltr', progress: 100, timeLeft: 3 });
    const toastTimerRef = useRef(null);
    const isSubmittingRef = useRef(false);

    const showToast = useCallback((type, message) => {
        if (toastTimerRef.current) clearInterval(toastTimerRef.current);
        const isRTL = currentLanguage === 'ur' || currentLanguage === 'ar';
        const direction = isRTL ? 'rtl' : 'ltr';
        setToast({ show: true, type, message, direction, progress: 100, timeLeft: 3 });
        let timeLeft = 3;
        const timer = setInterval(() => {
            timeLeft -= 0.1;
            const progress = (timeLeft / 3) * 100;
            setToast(prev => ({ ...prev, progress, timeLeft: parseFloat(timeLeft.toFixed(1)) }));
            if (timeLeft <= 0) { clearInterval(timer); setToast(prev => ({ ...prev, show: false })); }
        }, 100);
        toastTimerRef.current = timer;
    }, [currentLanguage]);

    useEffect(() => { return () => { if (toastTimerRef.current) clearInterval(toastTimerRef.current); }; }, []);

    const loadTranslations = useCallback(async (lang) => {
        if (!lang) return;
        setIsTranslating(true);
        try {
            const trans = await fieldOrdersTranslator.getTranslations(lang);
            setTranslations(trans);
            const isRTL = lang === 'ur' || lang === 'ar';
            document.documentElement.dir = isRTL ? 'rtl' : 'ltr';
            document.body.dir = isRTL ? 'rtl' : 'ltr';
        } catch (error) {
            console.error('Error loading translations:', error);
        } finally { setIsTranslating(false); }
    }, []);

    useEffect(() => {
        loadTranslations(currentLanguage);
        const handleLanguageChange = () => {
            const newLang = localStorage.getItem('ourzest_language') || 'en';
            if (newLang !== currentLanguage) { setCurrentLanguage(newLang); loadTranslations(newLang); }
        };
        window.addEventListener('storage', handleLanguageChange);
        const intervalId = setInterval(() => {
            const storedLang = localStorage.getItem('ourzest_language') || 'en';
            if (storedLang !== currentLanguage) { setCurrentLanguage(storedLang); loadTranslations(storedLang); }
        }, 500);
        const translationListener = (lang) => { if (lang !== currentLanguage) { setCurrentLanguage(lang); loadTranslations(lang); } };
        fieldOrdersTranslator.addListener(translationListener);
        return () => {
            window.removeEventListener('storage', handleLanguageChange);
            clearInterval(intervalId);
            fieldOrdersTranslator.removeListener(translationListener);
        };
    }, [currentLanguage, loadTranslations]);

    const loadBusinessInfo = useCallback(async () => {
        if (!user?.username) return;
        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/field_orders.php', {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ action: 'get_business_info', username: user.username })
            });
            const data = await response.json();
            if (data.status === 'success') setBusinessInfo(data.data);
        } catch (error) { console.error('Error loading business info:', error); }
    }, [user?.username]);

    useEffect(() => { if (currentShop) setShopInfo({ id: currentShop.shop_id, name: currentShop.shop_name }); }, [currentShop]);

    const applyTheme = useCallback((color) => {
        try {
            if (!/^#[0-9A-F]{6}$/i.test(color)) color = '#8b5cf6';
            const hex = color.replace('#', '');
            const r = parseInt(hex.substring(0, 2), 16);
            const g = parseInt(hex.substring(2, 4), 16);
            const b = parseInt(hex.substring(4, 6), 16);
            const adjust = (amount) => {
                let newR = Math.max(0, Math.min(255, r + amount));
                let newG = Math.max(0, Math.min(255, g + amount));
                let newB = Math.max(0, Math.min(255, b + amount));
                return `#${newR.toString(16).padStart(2, '0')}${newG.toString(16).padStart(2, '0')}${newB.toString(16).padStart(2, '0')}`;
            };
            const palette = {
                'purchase-primary-color': color,
                'purchase-primary-dark': adjust(-40),
                'purchase-primary-light': adjust(40),
                'purchase-primary-ultralight': adjust(20),
                'purchase-primary-gradient': `linear-gradient(135deg, ${color}, ${adjust(-20)})`,
                'purchase-primary-shadow': `${color}20`
            };
            Object.entries(palette).forEach(([key, value]) => document.documentElement.style.setProperty(`--${key}`, value));
        } catch (err) { console.error("Error applying theme:", err); }
    }, []);

    useEffect(() => {
        applyTheme(formData.themeColor);
        const handleThemeChange = () => {
            const theme = localStorage.getItem('ourzest_theme') || '#8b5cf6';
            if (theme !== formData.themeColor) { setFormData(prev => ({ ...prev, themeColor: theme })); applyTheme(theme); }
        };
        handleThemeChange();
        window.addEventListener('storage', handleThemeChange);
        const interval = setInterval(handleThemeChange, 1000);
        return () => { window.removeEventListener('storage', handleThemeChange); clearInterval(interval); };
    }, [formData.themeColor, applyTheme]);

    const getNextOrderNumber = async () => {
        if (!shopId) return;
        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/field_orders.php', {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ action: 'get_next_order_number', shop_id: shopId })
            });
            const data = await response.json();
            if (data.status === 'success') {
                setFormData(prev => ({ ...prev, order_number: data.data.order_number, order_number_int: data.data.order_number_int }));
            }
        } catch (error) { console.error('Error getting next order number:', error); }
    };

    useEffect(() => { if (shopId && !orderId) getNextOrderNumber(); loadBusinessInfo(); }, [shopId, orderId, loadBusinessInfo]);
    useEffect(() => { if (orderId) { setIsEditMode(true); loadOrderData(orderId); } }, [orderId]);
    useEffect(() => { calculateTotals(); }, [formData.items, formData.discount_percent, formData.tax_percent]);

    const loadOrderData = async (id) => {
        if (!user?.username || !shopId) return;
        setLoading(true);
        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/field_orders.php', {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ action: 'get_record', id, username: user.username, shop_id: shopId })
            });
            const data = await response.json();
            if (data.status === 'success' && data.data) {
                const order = data.data.order;
                const items = data.data.items;
                setFormData(prev => ({
                    ...prev,
                    order_date: order.order_date || new Date().toISOString().split('T')[0],
                    order_number: order.order_number || '',
                    order_number_int: order.order_number_int || 0,
                    customer_id: order.customer_id || null,
                    customer_name: order.customer_name || '',
                    customer_phone: order.customer_phone || '',
                    customer_address: order.customer_address || '',
                    customer_gst: order.customer_gst || '',
                    subtotal: order.subtotal || '0.00',
                    discount_percent: order.discount_percent || '0.00',
                    discount_amount: order.discount_amount || '0.00',
                    tax_percent: order.tax_percent || '0.00',
                    tax_amount: order.tax_amount || '0.00',
                    grand_total: order.grand_total || '0.00',
                    status: order.status || 'pending',
                    notes: order.notes || '',
                    items: items.map(item => ({
                        product_id: item.product_id,
                        product_name: item.product_name,
                        product_colorcode: item.product_colorcode,
                        batch_no: item.batch_no,
                        type: item.type,
                        brand: item.brand,
                        unit: item.unit || 'yards',
                        price_per_unit: parseFloat(item.price_per_unit) || 0,
                        quantity: parseFloat(item.quantity) || 0,
                        rolls_used: item.rolls_used || 0,
                        yards_per_roll: item.yards_per_roll || 0,
                        available_yards: item.total_yards || 0,
                        available_meters: item.total_meters || 0,
                        available_rolls: item.total_rolls || 0,
                        available_stock: item.unit === 'yards' ? (item.total_yards || 0) : (item.total_meters || 0),
                        stock_alert: 0
                    }))
                }));
                if (order.customer_id) {
                    setSelectedCustomer({
                        customer_id: order.customer_id,
                        customer_name: order.customer_name,
                        phone: order.customer_phone,
                        address: order.customer_address,
                        gst_no: order.customer_gst
                    });
                }
            }
        } catch (error) { showToast('error', translations?.networkError || 'Network error'); }
        finally { setLoading(false); }
    };

    const handleCustomerSelect = (customer) => {
        setSelectedCustomer(customer);
        setFormData(prev => ({
            ...prev,
            customer_id: customer.customer_id || customer.customerid,
            customer_name: customer.customer_name,
            customer_phone: customer.phone || '',
            customer_address: customer.address || '',
            customer_gst: customer.gst_no || ''
        }));
    };

    const handleProductSelect = (product) => {
        const existingIndex = formData.items.findIndex(item => item.product_id === product.id);
        
        const totalYards = parseFloat(product.total_yards) || 0;
        const totalMeters = parseFloat(product.total_meters) || 0;
        const totalRolls = parseInt(product.total_rolls) || 0;
        const stockAlert = parseFloat(product.stock_alert) || 0;
        
        if (totalYards <= 0 && totalMeters <= 0) {
            showToast('warning', translations?.outOfStock || 'Product is out of stock');
            return;
        }
        
        if (existingIndex >= 0) {
            const updatedItems = [...formData.items];
            const existingItem = updatedItems[existingIndex];
            const currentQty = parseFloat(existingItem.quantity) || 0;
            const availableStock = existingItem.unit === 'yards' ? totalYards : totalMeters;
            const newQty = currentQty + 1;
            
            if (newQty > availableStock) {
                showToast('warning', `${translations?.quantityExceedsStock || 'Quantity exceeds available stock'} (${availableStock.toFixed(2)} ${existingItem.unit})`);
                return;
            }
            updatedItems[existingIndex].quantity = newQty;
            setFormData(prev => ({ ...prev, items: updatedItems }));
            showToast('success', `Added another ${product.product_name}`);
        } else {
            const defaultUnit = product.unit || 'yards';
            
            const newItem = {
                product_id: product.id,
                product_name: product.product_name || '',
                product_colorcode: product.product_colorcode || '',
                batch_no: product.batch_no || '',
                type: product.type || '',
                brand: product.brand || '',
                unit: defaultUnit,
                price_per_unit: 0,
                quantity: 1,
                rolls_used: 0,
                yards_per_roll: 0,
                available_yards: totalYards,
                available_meters: totalMeters,
                available_rolls: totalRolls,
                available_stock: defaultUnit === 'yards' ? totalYards : totalMeters,
                stock_alert: stockAlert
            };
            setFormData(prev => ({ ...prev, items: [...prev.items, newItem] }));
            showToast('success', `${product.product_name} added to order`);
        }
    };

    const handleItemChange = (index, field, value, newAvailableStock = null) => {
        const updatedItems = [...formData.items];
        
        if (field === 'unit') {
            updatedItems[index] = { 
                ...updatedItems[index], 
                unit: value,
                available_stock: newAvailableStock !== null ? newAvailableStock : 
                    (value === 'yards' ? updatedItems[index].available_yards : updatedItems[index].available_meters)
            };
            
            const currentQty = parseFloat(updatedItems[index].quantity) || 0;
            if (currentQty > updatedItems[index].available_stock) {
                updatedItems[index].quantity = updatedItems[index].available_stock;
            }
        } else if (field === 'quantity') {
            const qty = parseFloat(value) || 0;
            const maxStock = updatedItems[index].available_stock || 0;
            
            updatedItems[index] = { ...updatedItems[index], quantity: qty };
            
            if (qty > maxStock) {
                setErrors(prev => ({ 
                    ...prev, 
                    [`quantity_${index}`]: `${translations?.quantityExceedsStock || 'Quantity exceeds stock'} (${maxStock.toFixed(2)})` 
                }));
            } else if (qty <= 0) {
                setErrors(prev => ({ 
                    ...prev, 
                    [`quantity_${index}`]: translations?.invalidQuantity || 'Invalid quantity' 
                }));
            } else {
                setErrors(prev => { 
                    const newErrors = { ...prev }; 
                    delete newErrors[`quantity_${index}`]; 
                    return newErrors; 
                });
            }
            
            // Auto-calculate rolls if yards_per_roll is set
            if (updatedItems[index].yards_per_roll > 0 && updatedItems[index].unit === 'yards') {
                const rollsFromQty = Math.ceil(qty / updatedItems[index].yards_per_roll);
                const maxRolls = updatedItems[index].available_rolls || 0;
                if (rollsFromQty <= maxRolls) {
                    updatedItems[index].rolls_used = rollsFromQty;
                }
            }
        } else if (field === 'rolls_used') {
            const rolls = parseInt(value) || 0;
            const maxRolls = updatedItems[index].available_rolls || 0;
            
            updatedItems[index] = { ...updatedItems[index], rolls_used: rolls };
            
            if (rolls > maxRolls) {
                setErrors(prev => ({ 
                    ...prev, 
                    [`rolls_${index}`]: `Rolls exceed available stock (${maxRolls})` 
                }));
            } else {
                setErrors(prev => { 
                    const newErrors = { ...prev }; 
                    delete newErrors[`rolls_${index}`]; 
                    return newErrors; 
                });
            }
            
            // Auto-calculate quantity if yards_per_roll is set
            if (updatedItems[index].yards_per_roll > 0 && updatedItems[index].unit === 'yards') {
                const qtyFromRolls = rolls * updatedItems[index].yards_per_roll;
                const maxStock = updatedItems[index].available_stock || 0;
                if (qtyFromRolls <= maxStock) {
                    updatedItems[index].quantity = qtyFromRolls;
                }
            }
        } else if (field === 'yards_per_roll') {
            const yardsPerRoll = parseFloat(value) || 0;
            updatedItems[index] = { ...updatedItems[index], yards_per_roll: yardsPerRoll };
            
            // Recalculate relationship between quantity and rolls
            if (yardsPerRoll > 0 && updatedItems[index].unit === 'yards') {
                const qty = parseFloat(updatedItems[index].quantity) || 0;
                const rolls = parseInt(updatedItems[index].rolls_used) || 0;
                
                if (qty > 0 && rolls === 0) {
                    const newRolls = Math.ceil(qty / yardsPerRoll);
                    const maxRolls = updatedItems[index].available_rolls || 0;
                    if (newRolls <= maxRolls) {
                        updatedItems[index].rolls_used = newRolls;
                    }
                } else if (rolls > 0 && qty === 0) {
                    const newQty = rolls * yardsPerRoll;
                    const maxStock = updatedItems[index].available_stock || 0;
                    if (newQty <= maxStock) {
                        updatedItems[index].quantity = newQty;
                    }
                }
            }
        } else if (field === 'price_per_unit') {
            updatedItems[index] = { ...updatedItems[index], price_per_unit: parseFloat(value) || 0 };
        } else {
            updatedItems[index] = { ...updatedItems[index], [field]: value };
        }
        
        setFormData(prev => ({ ...prev, items: updatedItems }));
    };

    const removeItem = (index) => {
        if (formData.items.length > 1) {
            const updatedItems = [...formData.items];
            updatedItems.splice(index, 1);
            setFormData(prev => ({ ...prev, items: updatedItems }));
        }
    };

    const calculateTotals = () => {
        let subtotal = 0;
        formData.items.forEach(item => {
            subtotal += (parseFloat(item.price_per_unit) || 0) * (parseFloat(item.quantity) || 0);
        });
        const discountPercent = parseFloat(formData.discount_percent) || 0;
        const discountAmount = (subtotal * discountPercent) / 100;
        const taxPercent = parseFloat(formData.tax_percent) || 0;
        const taxAmount = ((subtotal - discountAmount) * taxPercent) / 100;
        const grandTotal = subtotal - discountAmount + taxAmount;
        setFormData(prev => ({
            ...prev,
            subtotal: subtotal.toFixed(2),
            discount_amount: discountAmount.toFixed(2),
            tax_amount: taxAmount.toFixed(2),
            grand_total: grandTotal.toFixed(2)
        }));
    };

    const validateForm = () => {
        const newErrors = {};
        if (!formData.items || formData.items.length === 0) {
            newErrors.items = translations?.itemsRequired || 'At least one item is required';
        } else {
            formData.items.forEach((item, index) => {
                const qty = parseFloat(item.quantity) || 0;
                if (qty <= 0) newErrors[`quantity_${index}`] = translations?.invalidQuantity || 'Invalid quantity';
                const maxStock = item.available_stock || 0;
                if (qty > maxStock) newErrors[`quantity_${index}`] = `${translations?.quantityExceedsStock || 'Exceeds stock'} (${maxStock.toFixed(2)})`;
                if ((parseFloat(item.price_per_unit) || 0) <= 0) newErrors[`price_${index}`] = translations?.invalidPrice || 'Invalid price';
                
                const rollsUsed = parseInt(item.rolls_used) || 0;
                const maxRolls = item.available_rolls || 0;
                if (rollsUsed > maxRolls) newErrors[`rolls_${index}`] = `Rolls exceed available stock (${maxRolls})`;
            });
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        if (name === 'discount_percent' || name === 'tax_percent') calculateTotals();
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (isSubmittingRef.current) {
            showToast('warning', translations?.duplicateSubmission || 'Please wait, submission in progress...');
            return;
        }
        if (!shopId) { showToast('error', translations?.noShopSelected || 'No shop selected'); return; }
        if (!validateForm()) return;
        
        isSubmittingRef.current = true;
        setSaving(true);
        try {
            const currentUsername = user?.username || '';
            if (!currentUsername) throw new Error("User not authenticated");
            
            const payload = {
                action: isEditMode ? 'edit' : 'add',
                username: currentUsername,
                shop_id: shopId,
                order_date: formData.order_date,
                customer_id: formData.customer_id,
                customer_name: formData.customer_name,
                customer_phone: formData.customer_phone,
                customer_address: formData.customer_address,
                customer_gst: formData.customer_gst,
                subtotal: parseFloat(formData.subtotal),
                discount_percent: parseFloat(formData.discount_percent),
                discount_amount: parseFloat(formData.discount_amount),
                tax_percent: parseFloat(formData.tax_percent),
                tax_amount: parseFloat(formData.tax_amount),
                grand_total: parseFloat(formData.grand_total),
                status: formData.status,
                notes: formData.notes,
                items: formData.items.map(item => ({
                    product_id: item.product_id,
                    product_name: item.product_name,
                    product_colorcode: item.product_colorcode,
                    batch_no: item.batch_no,
                    type: item.type,
                    brand: item.brand,
                    unit: item.unit,
                    quantity: parseFloat(item.quantity),
                    price_per_unit: parseFloat(item.price_per_unit),
                    rolls_used: parseInt(item.rolls_used) || 0,
                    yards_per_roll: parseFloat(item.yards_per_roll) || 0
                }))
            };
            if (isEditMode && orderId) payload.id = orderId;
            
            const response = await fetch('https://ourzestapp.online/oz_backend/field_orders.php', {
                method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload)
            });
            const data = await response.json();
            if (data.status === 'success') {
                showToast('success', isEditMode ? (translations?.orderUpdated || 'Order updated!') : (translations?.orderCreated || 'Order created!'));
                if (!isEditMode) {
                    setFormData(prev => ({
                        ...prev,
                        order_date: new Date().toISOString().split('T')[0],
                        customer_id: null, customer_name: '', customer_phone: '', customer_address: '', customer_gst: '',
                        subtotal: '0.00', discount_percent: '0.00', discount_amount: '0.00',
                        tax_percent: '0.00', tax_amount: '0.00', grand_total: '0.00',
                        status: 'pending', notes: '', items: []
                    }));
                    setSelectedCustomer(null);
                    getNextOrderNumber();
                }
                if (onSuccess) onSuccess(data.data?.order_id || orderId);
            } else {
                showToast('error', data.message || (isEditMode ? translations?.failedUpdate : translations?.failedSave));
            }
        } catch (error) {
            showToast('error', error.message || translations?.networkError || 'Network error');
        } finally {
            setTimeout(() => {
                isSubmittingRef.current = false;
                setSaving(false);
            }, 1000);
        }
    };

    const handleClear = () => {
        setFormData(prev => ({
            ...prev,
            order_date: new Date().toISOString().split('T')[0],
            customer_id: null, customer_name: '', customer_phone: '', customer_address: '', customer_gst: '',
            subtotal: '0.00', discount_percent: '0.00', discount_amount: '0.00',
            tax_percent: '0.00', tax_amount: '0.00', grand_total: '0.00',
            status: 'pending', notes: '', items: []
        }));
        setSelectedCustomer(null);
        setErrors({});
        setIsEditMode(false);
        getNextOrderNumber();
    };

    const isRTL = currentLanguage === 'ur' || currentLanguage === 'ar';
    const statusOptions = [
        { value: 'pending', label: translations?.pending || 'Pending' },
        { value: 'processing', label: translations?.processing || 'Processing' },
        { value: 'approved', label: translations?.approved || 'Approved' },
        { value: 'delivered', label: translations?.delivered || 'Delivered' },
        { value: 'completed', label: translations?.completed || 'Completed' },
        { value: 'cancelled', label: translations?.cancelled || 'Cancelled' }
    ];

    return (
        <div className={`purchases-form-independent ${isRTL ? 'rtl' : ''} ${isTranslating ? 'translating' : ''}`}>
            <ToastNotification toast={toast} setToast={setToast} translations={translations} />
            
            <div className="form-header-modern">
                <div className="header-content">
                    <div className="header-icon"><FontAwesomeIcon icon={faShoppingBag} /></div>
                    <div className="header-text">
                        <h1>{isEditMode ? (translations?.editFieldOrder || 'Edit Field Order') : (translations?.newFieldOrder || 'New Field Order')}</h1>
                        <p>{translations?.createFieldOrder || 'Create Field Order'}</p>
                    </div>
                </div>
                {isTranslating && (<div className="translation-indicator"><FontAwesomeIcon icon={faSpinner} spin /><span>Translating...</span></div>)}
            </div>

            <BusinessInfo businessInfo={businessInfo} translations={translations} />

            <form onSubmit={handleSubmit} className="modern-form">
                {/* Order Information Section */}
                <div className="form-section">
                    <div className="section-header"><h3 className="section-title"><FontAwesomeIcon icon={faFileInvoice} />{translations?.orderDetails || 'Order Information'}</h3></div>
                    <div className="form-grid-modern">
                        <div className="form-group-modern">
                            <div className="input-container">
                                <FontAwesomeIcon icon={faFileInvoice} className="input-icon" />
                                <input type="text" id="order_number" value={formData.order_number} readOnly className="readonly-input" dir="ltr" placeholder=" " />
                                <label htmlFor="order_number" className="input-label">{translations?.orderNumber || 'Order Number'}</label>
                            </div>
                        </div>
                        <div className="form-group-modern">
                            <div className="input-container">
                                <FontAwesomeIcon icon={faStore} className="input-icon" />
                                <input type="text" value={shopInfo.name || shopId} readOnly className="readonly-input" dir={isRTL ? 'rtl' : 'ltr'} placeholder=" " />
                                <label className="input-label">{translations?.shop || 'Shop Name'}</label>
                            </div>
                        </div>
                        <div className="form-group-modern">
                            <div className="input-container">
                                <FontAwesomeIcon icon={faCalendarAlt} className="input-icon" />
                                <input type="date" id="order_date" name="order_date" value={formData.order_date} onChange={handleChange} disabled={saving} dir="ltr" placeholder=" " />
                                <label htmlFor="order_date" className="input-label">{translations?.date || 'Order Date'}</label>
                            </div>
                        </div>
                        <div className="form-group-modern">
                            <div className="input-container">
                                <FontAwesomeIcon icon={faTag} className="input-icon" />
                                <select id="status" name="status" value={formData.status} onChange={handleChange} disabled={saving} dir={isRTL ? 'rtl' : 'ltr'}>
                                    {statusOptions.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                                </select>
                                <label htmlFor="status" className="input-label">{translations?.orderStatus || 'Order Status'}</label>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Customer Section */}
                <div className="form-section">
                    <div className="section-header"><h3 className="section-title"><FontAwesomeIcon icon={faUser} />{translations?.customerDetails || 'Customer Details'}</h3></div>
                    <CustomerSearch onSelect={handleCustomerSelect} selectedCustomerId={formData.customer_id} selectedCustomerName={formData.customer_name} disabled={saving} translations={translations} isRTL={isRTL} shopId={shopId} user={user} showToast={showToast} />
                    <div className="form-grid-modern" style={{ marginTop: '15px' }}>
                        <div className="form-group-modern">
                            <div className="input-container">
                                <FontAwesomeIcon icon={faPhone} className="input-icon" />
                                <input type="tel" id="customer_phone" name="customer_phone" value={formData.customer_phone} onChange={handleChange} placeholder=" " disabled={saving} dir="ltr" />
                                <label htmlFor="customer_phone" className="input-label">{translations?.customerPhone || 'Phone'}</label>
                            </div>
                        </div>
                        <div className="form-group-modern full-width">
                            <div className="input-container">
                                <FontAwesomeIcon icon={faMapMarkerAlt} className="input-icon" />
                                <textarea id="customer_address" name="customer_address" value={formData.customer_address} onChange={handleChange} placeholder=" " disabled={saving} rows="2" dir={isRTL ? 'rtl' : 'ltr'} />
                                <label htmlFor="customer_address" className="input-label">{translations?.customerAddress || 'Address'}</label>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Products Section */}
                <div className="form-section">
                    <div className="section-header"><h3 className="section-title"><FontAwesomeIcon icon={faBox} />{translations?.searchProducts || 'Search Products'}</h3></div>
                    <ProductSearch onSelect={handleProductSelect} disabled={saving} translations={translations} isRTL={isRTL} shopId={shopId} user={user} showToast={showToast} />
                </div>

                {/* Items Table */}
                {formData.items.length > 0 && (
                    <div className="form-section">
                        <OrderItemsTable items={formData.items} onItemChange={handleItemChange} onRemove={removeItem} errors={errors} disabled={saving} translations={translations} isRTL={isRTL} />
                    </div>
                )}

                {/* Calculations */}
                {formData.items.length > 0 && (
                    <div className="form-section">
                        <div className="section-header"><h3 className="section-title"><FontAwesomeIcon icon={faCalculator} />{translations?.calculations || 'Calculations'}</h3></div>
                        <div className="calculations-grid fab-calculations">
                            <div className="calculation-row"><span className="calculation-label">{translations?.subtotal || 'Subtotal'}:</span><span className="calculation-value">Rs. {formData.subtotal}</span></div>
                            <div className="calculation-row">
                                <span className="calculation-label">{translations?.discountPercent || 'Discount %'}:</span>
                                <div className="discount-input-wrapper"><input type="number" id="discount_percent" name="discount_percent" value={formData.discount_percent} onChange={handleChange} step="0.01" min="0" max="100" placeholder="0.00" disabled={saving} dir="ltr" style={{ textAlign: 'right' }} /></div>
                                <span className="calculation-value negative">-Rs. {formData.discount_amount}</span>
                            </div>
                            <div className="calculation-row">
                                <span className="calculation-label">{translations?.taxPercent || 'Tax %'}:</span>
                                <div className="tax-input-wrapper"><input type="number" id="tax_percent" name="tax_percent" value={formData.tax_percent} onChange={handleChange} step="0.01" min="0" max="100" placeholder="0.00" disabled={saving} dir="ltr" style={{ textAlign: 'right' }} /></div>
                                <span className="calculation-value positive">+Rs. {formData.tax_amount}</span>
                            </div>
                            <div className="calculation-row grand-total">
                                <span className="calculation-label">{translations?.grandTotal || 'Grand Total'}:</span>
                                <span className="calculation-value highlight">Rs. {formData.grand_total}</span>
                            </div>
                            <div className="calculation-row">
                                <span className="calculation-label">{translations?.orderStatus || 'Order Status'}:</span>
                                <OrderStatusBadge status={formData.status} translations={translations} />
                            </div>
                        </div>
                    </div>
                )}

                {/* Notes */}
                <div className="form-section">
                    <div className="form-group-modern">
                        <div className="input-container textarea-container">
                            <FontAwesomeIcon icon={faReceipt} className="input-icon" />
                            <textarea id="notes" name="notes" value={formData.notes} onChange={handleChange} placeholder=" " disabled={saving} rows="3" dir={isRTL ? 'rtl' : 'ltr'} />
                            <label htmlFor="notes" className="input-label">{translations?.orderNotes || 'Order Notes'}</label>
                        </div>
                    </div>
                </div>

                {/* Actions */}
                <div className="form-actions-modern">
                    <button type="button" className="btn-modern btn-secondary-modern" onClick={handleClear} disabled={saving}>
                        <FontAwesomeIcon icon={faTimes} /><span>{translations?.clear || 'Clear'}</span>
                    </button>
                    <button type="submit" className="btn-modern btn-primary-modern" disabled={saving || isTranslating || formData.items.length === 0 || isSubmittingRef.current}>
                        {saving ? (<><div className="btn-spinner"></div><span>{translations?.saving || 'Saving...'}</span></>) : (<><span>{isEditMode ? (translations?.updateOrder || 'Update Order') : (translations?.createOrder || 'Create Order')}</span><FontAwesomeIcon icon={faSave} /></>)}
                    </button>
                </div>
            </form>

            {loading && (<div className="loading-overlay"><div className="loading-content"><div className="loading-spinner"></div><p>{translations?.loading || 'Loading...'}</p><p className="loading-subtext">Please wait while we load order data</p></div></div>)}
        </div>
    );
};

export default Field_Orders;